import Table from "components/DesignSystem/Table/V2/Table";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { BankLogo } from "components/icons/BankLogo";
import { TxnAmount } from "components/Transaction/TransactionColumn";
import { CategoryLabel } from "components/design/CategoryLabel";
import { CategorySelector } from "components/CategorySelector/CategorySelector";
import { SmartReviewTransaction } from "store/apis/transactions";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useState } from "react";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { ClampedTextWithTooltip } from "utils/ClampedTextWithTooltip";
import ToolTip from "components/design/toolTip";
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "react-table-8.10.7";

const columnHelper = createColumnHelper<SmartReviewTransaction>();

const baseColumns = [
  columnHelper.accessor("date", {
    id: "date",
    size: 110,
    enableSorting: true,
    sortingFn: "datetime",
    header: ({ column }) => (
      <HeadCellContent
        title="Date"
        sort={{
          isSorted: column.getIsSorted(),
          nextSortOrder: column.getNextSortingOrder(),
          sortingHandler: column.getToggleSortingHandler(),
        }}
      />
    ),
    cell: (info) => (
      <div className="t-text-body-sm t-text-text-30 t-whitespace-nowrap">
        {dayjs(info.getValue()).format(DISPLAY_DATE)}
      </div>
    ),
  }),

  columnHelper.accessor("from", {
    id: "source",
    size: 60,
    enableSorting: false,
    header: () => <HeadCellContent title="Source" align="center" />,
    cell: (info) => {
      const from = info.getValue();
      const logoUrl = from?.bank_account?.bank_brand?.logo_url;
      const sourceName =
        from?.bank_account?.nickname || from?.bank_account?.bank_brand?.name;
      const sourceIcon = logoUrl ? (
        <Avatar src={logoUrl} alt={sourceName || "Bank"} />
      ) : (
        <BankLogo />
      );
      return (
        <div className="t-flex t-w-full t-justify-center">
          {sourceName ? (
            <ToolTip text={sourceName}>
              <span>{sourceIcon}</span>
            </ToolTip>
          ) : (
            sourceIcon
          )}
        </div>
      );
    },
  }),

  columnHelper.accessor("description", {
    id: "description",
    size: 120,
    enableSorting: false,
    header: () => <HeadCellContent title="Description" />,
    cell: (info) => (
      <ClampedTextWithTooltip
        text={info.getValue() || "-"}
        lineClampElementClass="t-text-body-sm t-text-text-100 t-line-clamp-1"
      />
    ),
  }),

  columnHelper.accessor("amount", {
    id: "amount",
    size: 60,
    enableSorting: true,
    sortingFn: (rowA, rowB) =>
      parseFloat(rowA.original.amount) - parseFloat(rowB.original.amount),
    header: ({ column }) => (
      <HeadCellContent
        title="Amount"
        align="right"
        sort={{
          isSorted: column.getIsSorted(),
          nextSortOrder: column.getNextSortingOrder(),
          sortingHandler: column.getToggleSortingHandler(),
        }}
      />
    ),
    cell: (info) => <TxnAmount amount={parseFloat(info.getValue())} />,
  }),

  columnHelper.accessor("category", {
    id: "category",
    size: 200,
    enableSorting: false,
    header: () => <HeadCellContent title="Category" />,
    cell: (info) => {
      const { onCategoryChange, updatingCategoryIds } = info.table.options
        .meta as {
        onCategoryChange?: (txnId: string, categoryId: string | null) => void;
        updatingCategoryIds?: string[];
      };
      const cat = info.getValue();
      const txnId = info.row.original.id;

      if (onCategoryChange) {
        return (
          <CategorySelector
            withForm={false}
            category={cat?.uuid ?? null}
            isUpdating={updatingCategoryIds?.includes(txnId)}
            onChange={(val) => onCategoryChange(txnId, val?.value ?? null)}
            size="small"
            outline
          />
        );
      }

      if (!cat) return <span className="t-text-text-30">-</span>;
      return (
        <CategoryLabel
          category={{
            uuid: cat.uuid,
            name: cat.name,
            identifier: cat.identifier,
            description: "",
            identifier_prefix: 1,
            parent_id: "",
            category_type: undefined,
            parent_name: "",
            start_date: null,
            opening_balance: null,
            manually_added: false,
          }}
          indentLevel={0}
        />
      );
    },
  }),

  columnHelper.display({
    id: "confirming",
    size: 20,
    header: () => null,
    cell: ({ row, table }) => {
      const { confirmingIds } = table.options.meta as {
        confirmingIds: string[];
      };
      if (!confirmingIds?.includes(row.original.id)) return null;
      return (
        <div className="t-flex t-justify-end">
          <Spinner size="small" />
        </div>
      );
    },
  }),
];

type SmartReviewTxnTableProps<T extends SmartReviewTransaction> = {
  transactions: T[];
  isSelected: (txn: T) => boolean;
  isRowClickable: (txn: T) => boolean;
  onRowClick: (txn: T) => void;
  selectColumn: ColumnDef<T>;
  prefixColumns?: ColumnDef<T>[];
  confirmingIds?: string[];
  meta?: Record<string, unknown>;
};

export const SmartReviewTxnTable = <T extends SmartReviewTransaction>({
  transactions,
  isSelected,
  isRowClickable,
  onRowClick,
  selectColumn,
  prefixColumns = [],
  confirmingIds = [],
  meta = {},
}: SmartReviewTxnTableProps<T>) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = [
    selectColumn,
    ...prefixColumns,
    ...(baseColumns as unknown as ColumnDef<T>[]),
  ];

  const table = useReactTable({
    data: transactions,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: { confirmingIds, ...meta },
  });

  return (
    <Table.Container>
      <Table.Content>
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.HeadRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.HeadCell
                  key={header.id}
                  isUpperCase={false}
                  size="small"
                  className="t-group"
                  style={{
                    minWidth: header.column.getSize(),
                    maxWidth: header.column.getSize(),
                  }}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Table.HeadCell>
              ))}
            </Table.HeadRow>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row) => {
            const txn = row.original;
            const clickable = isRowClickable(txn);

            return (
              <Table.Row
                key={row.id}
                onRowClick={clickable ? () => onRowClick(txn) : undefined}
                isRowSelected={isSelected(txn)}
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell
                    key={cell.id}
                    size="small"
                    style={{
                      minWidth: cell.column.getSize(),
                      maxWidth: cell.column.getSize(),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Content>
    </Table.Container>
  );
};
